import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useHistory, useParams, Redirect } from "react-router-dom";
import {
  AppBar,
  Alert,
  Badge,
  Box,
  Button,
  Chip,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Snackbar,
  Tab,
  Tabs,
  Toolbar,
  Tooltip,
  Typography,
} from "@mui/material";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import CommentIcon from "@mui/icons-material/Comment";

import { MockDataProvider } from "./MockDataProvider";
import SnipOverlay from "./SnipOverlay";
import SnipCommentModal from "./SnipCommentModal";
import CommentPins from "./CommentPins";
import CommentDrawer from "./CommentDrawer";
import LiveEditChatPanel from "./LiveEditChatPanel";
import { getCommentsForScreen } from "./mockupApi";
import { captureSnippet } from "./captureSnippet";

const findScenario = (scenarios, scenarioId) =>
  scenarios.find((scenario) => scenario.id === scenarioId) || scenarios[0];

const findScreen = (scenario, screenId) => {
  if (!scenario) {
    return null;
  }
  const found = scenario.screens.find((screen) => screen.id === screenId);
  return found || scenario.screens[0];
};

const useViewportSize = () => {
  const [size, setSize] = useState({
    w: window.innerWidth,
    h: window.innerHeight,
  });
  useEffect(() => {
    const handleResize = () =>
      setSize({ w: window.innerWidth, h: window.innerHeight });
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);
  return size;
};

const useScreenComments = ({ hillId, scenarioId, screenRoute, refreshKey }) => {
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const refresh = useCallback(async () => {
    if (!hillId || !scenarioId || !screenRoute) {
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const fetched = await getCommentsForScreen({
        hillId,
        scenarioId,
        screenRoute,
      });
      setComments(fetched);
    } catch (err) {
      console.warn("[MockupShell] failed to load comments:", err?.message || err);
      setComments([]);
      setError(err?.message || "Failed to load comments");
    } finally {
      setLoading(false);
    }
  }, [hillId, scenarioId, screenRoute]);
  useEffect(() => {
    refresh();
  }, [refresh, refreshKey]);
  return { comments, loading, error, refresh };
};

const MockupShell = ({ hill }) => {
  const { hillKey, scenarioId, screen: screenId } = useParams();
  const history = useHistory();
  const viewport = useViewportSize();
  const screenContainerRef = useRef(null);
  const [snipMode, setSnipMode] = useState(false);
  const [pendingSnip, setPendingSnip] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedComment, setSelectedComment] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [snipError, setSnipError] = useState(null);
  const [liveOverrides, setLiveOverrides] = useState({});
  const handleLiveOverridesChange = useCallback((overrides) => setLiveOverrides(overrides), []);

  const activeScenario = useMemo(
    () => findScenario(hill.scenarios, scenarioId),
    [hill.scenarios, scenarioId],
  );
  const activeScreen = useMemo(
    () => findScreen(activeScenario, screenId),
    [activeScenario, screenId],
  );

  const needsRedirect =
    activeScenario &&
    activeScreen &&
    (activeScenario.id !== scenarioId || activeScreen.id !== screenId);

  const { comments, refresh } = useScreenComments({
    hillId: hill.hillId,
    scenarioId: activeScenario?.id,
    screenRoute: activeScreen?.route,
    refreshKey,
  });

  if (needsRedirect) {
    return (
      <Redirect
        to={`/mockups/${hillKey}/${activeScenario.id}/${activeScreen.id}`}
      />
    );
  }

  if (!activeScenario || !activeScreen) {
    return (
      <Box sx={{ p: 4 }}>
        <Typography>No scenarios registered for this mockup.</Typography>
      </Box>
    );
  }

  const handleScenarioChange = (event) => {
    const nextScenario = findScenario(hill.scenarios, event.target.value);
    history.push(
      `/mockups/${hillKey}/${nextScenario.id}/${nextScenario.screens[0].id}`,
    );
  };

  const handleScreenChange = (_event, newScreenId) =>
    history.push(`/mockups/${hillKey}/${activeScenario.id}/${newScreenId}`);

  const handleSnipComplete = async (rect) => {
    setSnipMode(false);
    setSnipError(null);
    try {
      const dataUrl = await captureSnippet({
        node: screenContainerRef.current,
        bbox: rect,
      });
      setPendingSnip({ bbox: rect, dataUrl });
    } catch (err) {
      console.warn("[MockupShell] capture failed:", err.message);
      setSnipError(err.message || "Snip capture failed");
    }
  };

  const handleSnipSubmitted = () => {
    setPendingSnip(null);
    setRefreshKey((prev) => prev + 1);
  };

  const handlePinSelect = (comment) => {
    setSelectedComment(comment);
    setDrawerOpen(true);
  };

  const liveOverride = liveOverrides[activeScreen.route];
  const ScreenComponent = liveOverride?.Component || activeScreen.Component;
  const knowledgeDocId = `hill-${hill.hillId.toLowerCase()}-ui-mockup`;
  const mockDataForScenario =
    hill.mockData && hill.mockData[activeScenario.id]
      ? hill.mockData[activeScenario.id]
      : {};

  return (
    <Box sx={{ minHeight: "100vh", display: "flex", flexDirection: "column" }}>
      <Snackbar
        open={Boolean(snipError)}
        autoHideDuration={6000}
        onClose={() => setSnipError(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          severity="error"
          variant="filled"
          onClose={() => setSnipError(null)}
        >
          {snipError}
        </Alert>
      </Snackbar>
      {liveOverride?.error && (
        <Alert severity="warning" data-snip-exclude="true">
          Couldn't render the mockup editor's live change on this screen: {liveOverride.error}
        </Alert>
      )}
      <AppBar
        position="sticky"
        color="default"
        elevation={1}
        data-snip-exclude="true"
      >
        <Toolbar sx={{ gap: 2, flexWrap: "wrap" }}>
          <Typography variant="h6" sx={{ mr: 2 }}>
            {hill.title}
          </Typography>
          <FormControl size="small" sx={{ minWidth: 240 }}>
            <InputLabel>Scenario</InputLabel>
            <Select
              value={activeScenario.id}
              label="Scenario"
              onChange={handleScenarioChange}
            >
              {hill.scenarios.map((scenario) => (
                <MenuItem key={scenario.id} value={scenario.id}>
                  {scenario.title}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {activeScenario.persona && (
            <Chip
              label={activeScenario.persona}
              size="small"
              color="primary"
              variant="outlined"
            />
          )}
          <Tabs
            value={activeScreen.id}
            onChange={handleScreenChange}
            variant="scrollable"
            scrollButtons="auto"
          >
            {activeScenario.screens.map((screen) => (
              <Tab key={screen.id} value={screen.id} label={screen.title} />
            ))}
          </Tabs>
          <Box sx={{ flexGrow: 1 }} />
          <Tooltip title={snipMode ? "Cancel snip" : "Snip a region to comment"}>
            <Button
              variant={snipMode ? "contained" : "outlined"}
              startIcon={<CommentIcon />}
              onClick={() => setSnipMode((prev) => !prev)}
            >
              {snipMode ? "Cancel" : "+ Comment"}
            </Button>
          </Tooltip>
          <Tooltip title="Open all comments on this screen">
            <IconButton
              onClick={() => {
                setSelectedComment(null);
                setDrawerOpen(true);
              }}
            >
              <Badge badgeContent={comments.length} color="primary">
                <ChatBubbleOutlineIcon />
              </Badge>
            </IconButton>
          </Tooltip>
        </Toolbar>
      </AppBar>
      <Box
        ref={screenContainerRef}
        sx={{ position: "relative", flexGrow: 1 }}
        key={`${activeScenario.id}/${activeScreen.id}`}
      >
        <MockDataProvider value={mockDataForScenario}>
          <ScreenComponent />
        </MockDataProvider>
        <CommentPins
          comments={comments}
          currentViewport={viewport}
          onSelect={handlePinSelect}
        />
        {snipMode && (
          <SnipOverlay
            containerRef={screenContainerRef}
            onComplete={handleSnipComplete}
            onCancel={() => setSnipMode(false)}
          />
        )}
      </Box>
      {pendingSnip && (
        <SnipCommentModal
          open={Boolean(pendingSnip)}
          onClose={() => setPendingSnip(null)}
          onSubmitted={handleSnipSubmitted}
          thumbnailDataUrl={pendingSnip.dataUrl}
          contextPayload={{
            hillId: hill.hillId,
            hillKey,
            scenarioId: activeScenario.id,
            scenarioName: activeScenario.title,
            persona: activeScenario.persona || null,
            screenRoute: activeScreen.route,
            screenTitle: activeScreen.title,
            bbox: pendingSnip.bbox,
            viewport,
            devicePixelRatio: window.devicePixelRatio || 1,
          }}
        />
      )}
      <CommentDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        selectedComment={selectedComment}
        comments={comments}
        hillId={hill.hillId}
        onSelect={(comment) => setSelectedComment(comment)}
        onReplied={() => {
          setSelectedComment(null);
          setRefreshKey((prev) => prev + 1);
          refresh();
        }}
      />
      <LiveEditChatPanel
        hillId={hill.hillId}
        hillKey={hillKey}
        knowledgeDocId={knowledgeDocId}
        scenarioId={activeScenario.id}
        scenarioName={activeScenario.title}
        screenRoute={activeScreen.route}
        screenTitle={activeScreen.title}
        onLiveOverridesChange={handleLiveOverridesChange}
      />
    </Box>
  );
};

export default MockupShell;
